/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { ISchemaViewRefModel, SCHEMA_VALUE_TYPE, TGenericModelNode } from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { InspectModel } from "../InspectModel";
import { FieldRow } from "../../Fields/FieldRow";
import { FieldNote } from "../../Fields/FieldNote";
import { Icon, ICON_SIZE } from "../../Icon";

export const ControlViewRef_Children: TInspectorControlComponent<ISchemaViewRefModel> = ({
	model: _model,
	path,
	depth
}) => {
	const { model } = useBlueprintModel(_model, "viewRef");
	const { model: viewIdModel } = useBlueprintModel(_model?.viewId);
	const { model: paramsModel } = useBlueprintModel(_model?.params);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<InspectModel model={viewIdModel} path={path.concat([ "viewId" ])} depth={depth} />
			{paramsModel?.value ? (
				<InspectModel model={paramsModel.value} path={path.concat([ "params" ])} depth={depth + 1} />
			) : viewIdModel.type === SCHEMA_VALUE_TYPE.CONST && viewIdModel.constant.value ? (
				<FieldRow>
					<FieldNote color="warning" padded>
						<Icon name="mdi/alert" size={ICON_SIZE.XSMALL} />
						{/* @todo Translation */}
						<span>View not found.</span>
					</FieldNote>
				</FieldRow>
			) : null}
		</>
	);
};

export const ControlViewRef = (model: TGenericModelNode): IInspectorControls => ({
	children: {
		component: ControlViewRef_Children,
		model: model
	}
});
